/**
 * Spec 087 — typed API client for sitemap coverage analyses.
 *
 *   POST /api/v1/brands/:brandId/sitemap-analyses        create + cost preview
 *   GET  /api/v1/sitemap-analyses/:id                   full record
 *   GET  /api/v1/sitemap-analyses/:id/urls              paginated URL inventory
 *   GET  /api/v1/sitemap-analyses/:id/recommendations   gap recommendations
 *   GET  /api/v1/brands/:brandId/sitemap-analyses       history
 *   POST /api/v1/sitemap-analyses/:id/cancel            mark cancelled
 *
 * The POST endpoint both estimates cost and persists the analysis row in a
 * single call — there is NO dry-run variant. The client must surface 402
 * responses (budget_exceeded) as a recoverable UI state without losing the
 * user's form entry.
 *
 * Types intentionally duplicated from server routes; the Vite client and
 * the Fastify server share no module graph.
 */
import { apiFetch, apiGet } from '../lib/api.js';

export type SitemapUrlTier = 'tier_1' | 'tier_2' | 'tier_3' | 'tier_4';
export type SitemapUrlEvaluationStatus =
  | 'skipped'
  | 'pending'
  | 'evaluating'
  | 'completed'
  | 'failed';

export interface TierPreview {
  tier_1: number;
  tier_2: number;
  tier_3: number;
  tier_4: number;
  to_evaluate: number;
}

export interface StartAnalysisRequest {
  /** Optional; defaults to `${brand.domain}/sitemap.xml`. */
  sitemap_url?: string;
  /** Per-tier budget. Defaults to `{ tier_3: 50 }`. */
  tier_budgets?: { tier_3?: number };
  /** Include Tier 4 (low-priority) pages. Defaults to false. */
  include_tier_4?: boolean;
  /** Bypass monthly budget cap. Admin-only; server returns 403 otherwise. */
  allow_budget_override?: boolean;
}

export interface StartAnalysisResponse {
  analysis_id: string;
  status: 'pending';
  estimated_cost_usd: number;
  cost_cap_usd: number;
  tier_preview: TierPreview;
}

export interface BudgetExceededResponse {
  error: 'budget_exceeded';
  code: string;
  estimated_cost_usd: number;
  monthly_cost_cap_usd: number;
  monthly_spend_so_far: number;
  tier_preview: TierPreview;
}

/**
 * Discriminated union returned by `startSitemapAnalysis`. Callers handle
 * each case explicitly — the dialog surfaces 402 inline, closes on 201, and
 * toasts on other terminal errors.
 */
export type StartAnalysisResult =
  | { kind: 'created'; data: StartAnalysisResponse }
  | { kind: 'budget_exceeded'; data: BudgetExceededResponse }
  | { kind: 'flag_disabled'; flag: string }
  | { kind: 'admin_required' }
  | { kind: 'validation_error'; message: string }
  | { kind: 'preview_failed'; message: string }
  | { kind: 'unknown_error'; status: number; message: string };

function safeMessage(body: unknown, fallback: string): string {
  if (
    body &&
    typeof body === 'object' &&
    'error' in body &&
    typeof (body as { error: unknown }).error === 'string'
  ) {
    const err = (body as { error: string }).error;
    if ('detail' in body && typeof (body as { detail: unknown }).detail === 'string') {
      return `${err}: ${(body as { detail: string }).detail}`;
    }
    return err;
  }
  return fallback;
}

export async function startSitemapAnalysis(
  brandId: string,
  body: StartAnalysisRequest,
): Promise<StartAnalysisResult> {
  const res = await apiFetch(`/api/v1/brands/${brandId}/sitemap-analyses`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

  let parsed: unknown = null;
  try {
    parsed = await res.json();
  } catch {
    parsed = null;
  }

  if (res.status === 201 && parsed && typeof parsed === 'object') {
    return { kind: 'created', data: parsed as StartAnalysisResponse };
  }
  if (res.status === 402 && parsed && typeof parsed === 'object') {
    return { kind: 'budget_exceeded', data: parsed as BudgetExceededResponse };
  }
  if (res.status === 403) {
    return { kind: 'admin_required' };
  }
  if (res.status === 503 && parsed && typeof parsed === 'object' && 'flag' in parsed) {
    return {
      kind: 'flag_disabled',
      flag: String((parsed as { flag: unknown }).flag ?? 'unknown'),
    };
  }
  if (res.status === 400) {
    return {
      kind: 'validation_error',
      message: safeMessage(parsed, 'Validation error'),
    };
  }
  if (res.status === 502) {
    return {
      kind: 'preview_failed',
      message: safeMessage(parsed, 'Sitemap preview failed'),
    };
  }
  return {
    kind: 'unknown_error',
    status: res.status,
    message: safeMessage(parsed, `HTTP ${res.status}`),
  };
}

/* ─── URL inventory ────────────────────────────────────────────── */

export interface SitemapUrlRow {
  id: string;
  url: string;
  lastmod: string | null;
  priority_from_sitemap: number | null;
  tier: SitemapUrlTier;
  triage_signals: Record<string, unknown>;
  evaluation_status: SitemapUrlEvaluationStatus;
  page_evaluation_id: string | null;
  assigned_cluster_slugs: string[];
  created_at: string;
}

export interface ListUrlsFilters {
  tier?: SitemapUrlTier;
  evaluation_status?: SitemapUrlEvaluationStatus;
  limit?: number;
  offset?: number;
}

export interface ListUrlsResponse {
  data: SitemapUrlRow[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Server serializes empty Postgres `text[]` columns as "{}" on some
 * rows — mirror the defensive normalization pattern from `api/rules.ts`
 * so every downstream consumer sees a real array.
 */
function normalizePgArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '{}') return [];
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      const inner = trimmed.slice(1, -1);
      if (inner.length === 0) return [];
      return inner
        .split(',')
        .map((s) => s.replace(/^"|"$/g, '').trim())
        .filter((s) => s.length > 0);
    }
  }
  return [];
}

function normalizeUrlRow(raw: SitemapUrlRow): SitemapUrlRow {
  return {
    ...raw,
    assigned_cluster_slugs: normalizePgArray(raw.assigned_cluster_slugs),
    triage_signals:
      raw.triage_signals && typeof raw.triage_signals === 'object'
        ? raw.triage_signals
        : {},
  };
}

export async function listSitemapUrls(
  analysisId: string,
  filters: ListUrlsFilters = {},
): Promise<ListUrlsResponse> {
  const q = new URLSearchParams();
  if (filters.tier) q.set('tier', filters.tier);
  if (filters.evaluation_status) q.set('evaluation_status', filters.evaluation_status);
  q.set('limit', String(Math.min(Math.max(filters.limit ?? 500, 1), 500)));
  q.set('offset', String(Math.max(filters.offset ?? 0, 0)));
  const url = `/api/v1/sitemap-analyses/${analysisId}/urls?${q}`;
  const res = await apiGet<ListUrlsResponse>(url);
  return {
    ...res,
    data: Array.isArray(res.data) ? res.data.map(normalizeUrlRow) : [],
  };
}

export interface CoverageResetResponse {
  brand_id: string;
  deleted: {
    sitemap_analyses: number;
    page_evaluations: number;
  };
}

/** Wipes ALL sitemap_analyses + page_evaluations rows for the brand (and
 * everything that cascades off them). Brand config and the rule library
 * are preserved. */
export async function resetCoverageData(
  brandId: string,
): Promise<CoverageResetResponse> {
  const res = await apiFetch(`/api/v1/brands/${brandId}/coverage/reset`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Reset failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: CoverageResetResponse };
  return json.data;
}
